import { Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WidgetSettingsDto } from '@/Domain/api/WIdget/dto/WidgetSetting.dto';
import { WidgetTranslationDto } from '@/Domain/api/WIdget/dto/WidgetTranslation.dto';
import { cn } from '@/Domain/utils/cn';
import { AddLanguagePopover } from '@/UI/Website/TextContent/AddLanguagePopover';
import { DeleteLanguageDialog } from '@/UI/Website/TextContent/DeleteLanguageDialog';
import {
  getTranslationStatus,
  TranslationStatus,
} from '@/UI/Website/TextContent/translationStatus';

export type TranslationTarget = { language: string; country: string };

const isSameTarget = (a: TranslationTarget | null, b: TranslationTarget): boolean =>
  !!a && a.language === b.language && a.country === b.country;

// Neutral progress: how many of the fields the primary fills are translated for
// this language. Nothing is required (empty fields fall back to the default), so
// this is informational, not an obligation.
const StatusBadge = ({ status }: { status: TranslationStatus }) => {
  if (status.total === 0) return null;
  return (
    <span
      data-testid="rail-status"
      className={cn(
        'shrink-0 rounded-full px-2 py-0.5 text-xs font-medium tabular-nums',
        status.complete ? 'bg-success-muted text-success' : 'bg-muted text-muted-foreground',
      )}
    >
      {status.filled}/{status.total}
    </span>
  );
};

type Props = {
  primary: WidgetSettingsDto;
  translations: WidgetTranslationDto[];
  selection: TranslationTarget | null;
  onSelect: (selection: TranslationTarget) => void;
  onAdd: (language: string) => void;
  onDelete: (language: string, country: string) => void;
};

export const LanguageRail = ({
  primary,
  translations,
  selection,
  onSelect,
  onAdd,
  onDelete,
}: Props) => {
  const { t } = useTranslation(['website', 'common']);
  const [pendingDelete, setPendingDelete] = useState<TranslationTarget | null>(null);

  const used = useMemo(() => new Set(translations.map((row) => row.language)), [translations]);

  // Stable alphabetical order by the displayed language name. Optimistic cache
  // updates re-append the edited row, so without this the rail would reorder
  // ("last edited" first) as the user types.
  const label = (row: TranslationTarget) =>
    `${t(`languages.${row.language}`, { ns: 'common' })}${row.country ? ` (${row.country})` : ''}`;
  const sortedTranslations = useMemo(
    () => [...translations].sort((a, b) => label(a).localeCompare(label(b))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translations, t],
  );

  const pendingLabel = pendingDelete
    ? t(`languages.${pendingDelete.language}`, { ns: 'common' })
    : '';

  return (
    <div
      className="flex w-48 shrink-0 flex-col border-r border-border bg-muted/30 sm:w-60"
      data-testid="language-rail"
    >
      <div className="flex items-center justify-between gap-2 px-3 pb-2 pt-4">
        <span className="text-sm font-medium">{t('text_content_languages.section_title')}</span>
        {translations.length > 0 && (
          <span className="text-xs text-foreground-subtle">{translations.length}</span>
        )}
      </div>

      <div className="flex flex-col gap-2 px-3 pb-2">
        <AddLanguagePopover used={used} onAdd={onAdd} triggerClassName="w-full" />
      </div>

      <div className="custom-scroll flex-1 overflow-y-auto px-3 pb-2 gap-1">
        {sortedTranslations.map((row) => {
          const target = { language: row.language, country: row.country };
          const active = isSameTarget(selection, target);
          return (
            <div
              key={`${row.language}_${row.country}`}
              className={cn(
                'flex items-center gap-1 rounded-xl',
                active ? 'bg-accent' : 'hover:bg-accent/60',
              )}
            >
              <button
                type="button"
                onClick={() => onSelect(target)}
                className="flex min-w-0 flex-1 items-center gap-2 px-2.5 py-1.5 text-left text-sm"
                data-testid={`rail-language-${row.language}`}
              >
                <span className="min-w-0 flex-1 truncate">
                  {t(`languages.${row.language}`, { ns: 'common' })}
                  {row.country ? ` (${row.country})` : ''}
                </span>
                <StatusBadge status={getTranslationStatus(primary, row)} />
              </button>
              <button
                type="button"
                aria-label={t('text_content_languages.remove')}
                onClick={() => setPendingDelete(target)}
                data-testid={`rail-remove-${row.language}`}
                className="mr-1.5 rounded-md p-1 text-foreground-subtle transition-colors hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>

      <DeleteLanguageDialog
        open={pendingDelete !== null}
        languageLabel={pendingLabel}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        onConfirm={() => {
          if (pendingDelete) onDelete(pendingDelete.language, pendingDelete.country);
          setPendingDelete(null);
        }}
      />
    </div>
  );
};
